import { LinkProps } from "@vixi/router";
import { FC } from "react";
import { cn, Img, UnstyledLink } from "@vixi/ui";
import { cva, VariantProps } from "class-variance-authority";

// Constants and Defaults
export const BASIC_CARD_DEFAULT_WIDTH = 264;

interface BasicCardProps extends LinkProps, VariantProps<typeof basicCardVariants> {
  title: string;
  description: string;
  icon?: string | React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const basicCardVariants = cva("flex ms-1 align-bottom relative rounded", {
  variants: {
    variant: {
      outlined: "border-2 border-primary",
    },
    color: {
      primary: "bg-primary-light hover:bg-primary text-primary-foreground",
      paper:
        "text-black bg-paper border-2 border-paper hover:border-primary transition-colors duration-200",
    },
    usage: {
      "create-course": "w-66 h-25.75 items-center px-4",
      "premium-feature": "h-full justify-center",
    },
    disableHover: {
      true: "pointer-events-none",
      false: "",
    },
  },
});

/**
 * @param props.variant
 * @param props.color The color of the card
 * @param props.title The title of the card
 * @param props.description The description of the card
 * @param props.icon The icon of the card (either the src of an image or a React node)
 */
export const BasicCard: FC<BasicCardProps> = ({
  variant,
  color,
  title,
  description,
  icon,
  to,
  usage,
  disableHover,
  onClick,
}) => {
  return (
    <UnstyledLink
      className={cn(basicCardVariants({ variant, color: color ?? "paper", usage, disableHover }))}
      to={to}
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        {usage === "premium-feature" ? (
          <>
            <div className="ms-5 h-[150px] flex flex-col justify-center">
              <h4 className="fs-23 leading-normal ar:text-black ar:font-semibold">
                {title}
              </h4>
              <p className="fw-400 py-2 fs-16 leading-none">
                {description}
              </p>
            </div>
            {typeof icon === "string" ? <Img contain className="w-full h-full" src={icon} /> : icon}
          </>
        ) : (
          <>
            {typeof icon === "string" ? <Img contain className="h-13.75 w-11.5" src={icon} /> : icon}
            <div>
              <h4 className="ar:text-black ar:font-semibold">
                {title}
              </h4>
              <p
                className={cn("fw-400", {
                  "fs-13": usage === "create-course",
                })}
              >
                {description}
              </p>
            </div>
          </>
        )}
      </div>
    </UnstyledLink>
  );
};

export default BasicCard;
